import { IndicatorNumber, TextInfoGroupAlt } from "@/components/shared";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import { Shift, TenderDurationFragment } from "@/graphql/__generated__/graphql";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { formatDateLocale } from "@/utils/helpers";
import { Badge } from "@/components/ui/badge";

interface Props {
  shifts: Shift[];
  tenderDuration: TenderDurationFragment;
}

/**
 * ShiftsSection - Renders the shifts accordion section in the Calculation Overview sidebar.
 *
 * @component
 * @example
 * <ShiftsSection shifts={service.shifts} tenderDuration={tenderDuration} />
 */
function ShiftsSection({ shifts, tenderDuration }: Props) {
  const intl = useIntl();
  const { t } = useTranslation();

  const hasShifts = (shifts?.length ?? 0) > 0;

  return (
    <Card className="shadow-none">
      <Accordion type="single" collapsible>
        <AccordionItem value="shifts">
          <AccordionTrigger className="hover:no-underline hover:bg-slate-50 px-4 py-4">
            <div className="flex items-center gap-2 w-full">
              <div className="text-sm font-bold grow">{t("edittender.calculationoverview.components.shifts.title")}</div>
              <IndicatorNumber number={shifts.length} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-2 pb-4">
            {!hasShifts ? (
              <div className="text-sm text-slate-500 text-center pt-4">{t("edittender.calculationoverview.components.shifts.empty")}</div>
            ) : (
              <div className="flex flex-col gap-2">
                {shifts.map((shift) => (
                  <Card key={shift.id} className="flex flex-col gap-2 shadow-none p-2 hover:bg-slate-50">
                    {/* Shift Details */}
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {/* Shift Duration */}
                      <TextInfoGroupAlt label={t("tables:shifts.columns.shiftduration")} content={`${shift.startTime}` + " - " + `${shift.endTime}`} />
                      {/* Employees */}
                      <TextInfoGroupAlt label={t("tables:shifts.columns.employees")} content={shift.employeeQuantity} />
                      {/* Holiday Pay */}
                      <TextInfoGroupAlt label={t("tables:shifts.columns.holidaypay")} content={shift.applyHolidayPay ? t("common:yes") : t("common:no")} />
                      {/* Date Range */}
                      <TextInfoGroupAlt label={t("tables:shifts.columns.daterange")} content={
                        !shift.startDate && !shift.endDate
                          ? t("common:entirecontractduration")
                          : `${shift.startDate ? formatDateLocale(intl, shift.startDate) : formatDateLocale(intl, tenderDuration.contract.startDate)}` +
                          " - " +
                          `${shift.endDate ? formatDateLocale(intl, shift.endDate) : formatDateLocale(intl, tenderDuration.contract.endDate)}`
                      } />
                      {/* Pause Duration */}
                      {shift.pauseStartTime && shift.pauseEndTime && (
                        <TextInfoGroupAlt label={t("tables:shifts.columns.pauseduration")} content={`${shift.pauseStartTime} - ${shift.pauseEndTime}`} />
                      )}
                    </div>

                    {/* Shift Days */}
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries({
                            monday: shift.monday,
                            tuesday: shift.tuesday,
                            wednesday: shift.wednesday,
                            thursday: shift.thursday,
                            friday: shift.friday,
                            saturday: shift.saturday,
                            sunday: shift.sunday,
                            holiday: shift.holiday,
                          })
                            .filter(([, isActive]) => isActive)
                            .map(([day]) => (
                              <Badge key={day} variant="secondary" className="text-xs bg-blue-100">
                                {t("common:weekdaysshort." + day)}
                              </Badge>
                            ))}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

export default ShiftsSection;
